import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

export type LessonStatus = "In Progress" | "Completed" | "Failed";

export interface InteractionBuffer {
  id?: string;
  type?: string;
  result?: string;
  studentResponse?: string;
  weighting?: string;
  latency?: string;
}

export function parseLearnerName(params: { name: string; firstName?: string; lastName?: string }): {
  firstName: string;
  lastName: string;
} {
  const { name, firstName, lastName } = params;

  if (isDefined(firstName) && firstName.length > 0 && isDefined(lastName) && lastName.length > 0) {
    return { firstName, lastName };
  }

  const splitName = name.trim().split(" ");
  return {
    firstName: (splitName[0] ?? "").trim() || "Unknown",
    lastName: splitName.slice(1).join(" ").trim() || "Learner",
  };
}

export function buildContentUrl(params: {
  contentUrlTemplate: string;
  dispatchRoot: string;
  learnerId: string;
  learnerFirstName: string;
  learnerLastName: string;
}): string {
  const { contentUrlTemplate, dispatchRoot, learnerId, learnerFirstName, learnerLastName } = params;

  const pipeUrl = `${window.location.protocol}//${window.location.host}/`;
  const redirectUrl = `${dispatchRoot}Closer.html`;

  const replacements: Record<string, string> = {
    "{{learner_id}}": encodeURIComponent(learnerId),
    "{{learner_first_name}}": encodeURIComponent(learnerFirstName),
    "{{learner_last_name}}": encodeURIComponent(learnerLastName),
    "{{pipe_url}}": encodeURIComponent(pipeUrl),
    "{{redirect_url}}": encodeURIComponent(redirectUrl),
    // cspell:ignore regid
    "{{registration_argument}}": "&regid=_none_",
  };

  return Object.entries(replacements).reduce(
    (url, [token, value]) => url.split(token).join(value),
    contentUrlTemplate
  );
}

function parseScormNumber(value: string): number | undefined {
  if (value === "") {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function readScorePercent(params: {
  getValue: (element: string) => string;
}): number | undefined {
  const { getValue } = params;

  const rawValue = getValue("cmi.core.score.raw");
  const maxValue = getValue("cmi.core.score.max");
  const minValue = getValue("cmi.core.score.min");

  const raw = parseScormNumber(rawValue);
  if (!isDefined(raw)) {
    return undefined;
  }

  const max = parseScormNumber(maxValue) ?? 100;
  const min = parseScormNumber(minValue) ?? 0;

  if (max === min) {
    return undefined;
  }

  const percent = ((raw - min) / (max - min)) * 100;
  return Math.max(0, Math.min(100, percent));
}

function wrapScormMethod(params: {
  original: (...parameters: string[]) => string;
  methodName: string;
  courseId: string;
  requirementId: string;
  onStatusChange: (status: LessonStatus) => void;
  onCourseLoaded: () => void;
  interactionBuffers: Map<string, InteractionBuffer>;
}): (...parameters: string[]) => string {
  const {
    original,
    methodName,
    courseId,
    requirementId,
    onStatusChange,
    onCourseLoaded,
    interactionBuffers,
  } = params;

  return (...parameters: string[]): string => {
    const result = original(...parameters);
    const [element, value] = parameters;

    if (methodName === "LMSCommit") {
      onCourseLoaded();
    }

    if (methodName !== "LMSSetValue" || !isDefined(element)) {
      return result;
    }

    if (element === "cmi.core.lesson_status") {
      if (value === "passed" || value === "completed") {
        onStatusChange("Completed");
      } else if (value === "failed") {
        onStatusChange("Failed");
      }
    }

    if (element === "cmi.core.score.raw") {
      logEvent(APP_V2_APP_EVENTS.COURSE_SCORE_UPDATED, {
        course_id: courseId,
        requirement_id: requirementId,
        score_raw: Number(value),
      });
    }

    const interactionMatch = /^cmi\.interactions\.(\d+)\.(\w+)/.exec(element);
    if (isDefined(interactionMatch)) {
      const [, indexKey, field] = interactionMatch;
      if (!isDefined(indexKey) || !isDefined(field)) {
        return result;
      }
      const buffer = interactionBuffers.get(indexKey) ?? {};

      switch (field) {
        case "id": {
          buffer.id = value;
          break;
        }

        case "type": {
          buffer.type = value;
          break;
        }

        case "result": {
          buffer.result = value;
          break;
        }

        case "student_response": {
          buffer.studentResponse = value;
          break;
        }

        case "weighting": {
          buffer.weighting = value;
          break;
        }

        case "latency": {
          buffer.latency = value;
          break;
        }

        default: {
          break;
        }
      }

      interactionBuffers.set(indexKey, buffer);

      if (isDefined(buffer.id) && isDefined(buffer.result)) {
        logEvent(APP_V2_APP_EVENTS.COURSE_INTERACTION_ANSWERED, {
          course_id: courseId,
          requirement_id: requirementId,
          interaction_id: buffer.id,
          interaction_type: buffer.type,
          result: buffer.result,
          student_response: buffer.studentResponse,
          weighting: buffer.weighting,
          latency: buffer.latency,
        });
        interactionBuffers.delete(indexKey);
      }
    }

    return result;
  };
}

export function wrapApiMethods(params: {
  api: Record<string, (...parameters: string[]) => string>;
  methods: readonly string[];
  courseId: string;
  requirementId: string;
  onStatusChange: (status: LessonStatus) => void;
  onCourseLoaded: () => void;
  interactionBuffers: Map<string, InteractionBuffer>;
}): void {
  const {
    api,
    methods,
    courseId,
    requirementId,
    onStatusChange,
    onCourseLoaded,
    interactionBuffers,
  } = params;

  methods.forEach((method) => {
    const original = (api[method] as (...parameters: string[]) => string).bind(api);
    api[method] = wrapScormMethod({
      original,
      methodName: method,
      courseId,
      requirementId,
      onStatusChange,
      onCourseLoaded,
      interactionBuffers,
    });
  });
}
